import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useEffect, useMemo, useRef } from 'react';

import { Accordion } from '@sravni/react-design-system';
import DetailsTabRow from '@sravni/vitrina/lib/enums/config/DetailsTabRow';
import getProperty from '@sravni/vitrina/lib/helpers/getProperty';
import { getVariantHelper } from '@sravni/vitrina/lib/helpers/getVariantHelper';
import type ITabConfig from '@sravni/vitrina/lib/types/config/details/ITabConfig';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';
import { Wysiwyg } from '@sravni/vitrina-components';

import DynamicProductTable from '@components/RatingTable/components/ProductPopup/components/DynamicProductTable';
import ProductTable from '@components/RatingTable/components/ProductPopup/components/ProductTable';
import { vitrinsFilters } from '@components/RatingTable/components/ProductPopup/constants/filters';
import { useDictionaries, useProductConfig } from 'src/store/hooks/productPopupInfo';

import { groupDetails } from '../../helpers/groupDetails';
import type ITabDetail from '../../interfaces/ITabDetail';
import { dragToClick } from '../../utils/dragToClick';
import CarouselRow from '../CarouselRow';
import Detail from '../Detail';
import DownloadDocument from '../Detail/Document';

import style from './style.module.scss';

interface ITabsProps {
  className?: string;
  rowClassName?: string;
  classNameItem?: string;
  defaultIndex?: number[];
  product: IProduct;
  config: ITabConfig[];
  isProductPage?: boolean;
}

const filters = vitrinsFilters;

const Tabs: React.FC<ITabsProps> = memo((props) => {
  const { className, rowClassName, classNameItem, defaultIndex, product, config, isProductPage } = props;
  const dictionaries = useDictionaries();

  const vitrinConfig = useProductConfig();

  const tabs = useMemo(
    (): ITabDetail[] =>
      config?.reduce((out, detail): ITabDetail[] => {
        if (detail.dynamicTable) {
          out.push({
            ...detail,
          });

          return out;
        }

        if (detail.table) {
          if (isEmpty(product[detail.table.tableName])) {
            return out;
          }

          out.push({
            ...detail,
          });

          return out;
        }

        if (detail.carousel) {
          if (isEmpty(product[detail.carousel.property])) {
            return out;
          }

          out.push({
            ...detail,
          });

          return out;
        }

        const rowsValue = detail.rows.reduce((acc, item) => {
          const itemDetail = item.variants ? getVariantHelper(item.variants, filters, product) : item;

          const commonResult = {
            label: itemDetail?.label,
            type: itemDetail?.value?.type,
            labelTooltip: itemDetail?.labelTooltip,
            valueTooltip: itemDetail?.valueTooltip,
          };

          if (itemDetail?.value?.type === DetailsTabRow.Template) {
            const value = Object.entries(itemDetail.value.properties).reduce(
              // eslint-disable-next-line max-nested-callbacks
              (accProp, [key, property]) =>
                accProp.replace(`%${key}%`, getProperty(vitrinConfig, property, product, dictionaries) as string),
              itemDetail.value.template,
            );

            if (value) {
              acc.push({
                ...commonResult,
                value,
              });
            }

            return acc;
          }

          const value = getProperty(vitrinConfig, itemDetail?.value, product, dictionaries);
          const isWysiwygType =
            itemDetail?.value?.type === DetailsTabRow.Wysiwyg ||
            itemDetail?.value?.type === DetailsTabRow.TemplateWysiwyg;
          const isDocument = itemDetail?.value?.type === DetailsTabRow.Document;

          if (value) {
            acc.push({
              ...commonResult,
              // eslint-disable-next-line no-nested-ternary
              value: isWysiwygType ? (
                <Wysiwyg content={value as string} />
              ) : isDocument ? (
                <DownloadDocument text={value[1]} link={value[0]} />
              ) : (
                value
              ),
            });
          }

          return acc;
        }, []);

        if (rowsValue.length) {
          out.push({
            ...detail,
            rows: groupDetails(rowsValue),
          });
        }

        return out;
      }, []),
    [config, dictionaries, product, vitrinConfig],
  );

  const ref = useRef<HTMLElement>(null);

  useEffect(() => dragToClick(ref.current?.children[0] as HTMLElement), []);

  const accordionIndexes = useMemo(
    () => {
      if (defaultIndex) {
        return defaultIndex;
      }

      return tabs.reduce((acc, item, index) => {
        if (item.isAccordionOpen) {
          acc.push(index);
        }

        return acc;
      }, []);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultIndex],
  );

  const renderContent = (detail: ITabDetail) => {
    if (detail.table) {
      return (
        <ProductTable
          isProductPage={isProductPage}
          config={detail}
          product={product}
          additionalInformationClassName={style.additionalInformation}
        />
      );
    }

    if (detail.dynamicTable) {
      return <DynamicProductTable isProductPage={isProductPage} config={detail} product={product} />;
    }

    if (detail.carousel) {
      return (
        <CarouselRow
          config={detail}
          product={product}
          rowClassName={rowClassName}
          additionalInformationClassName={style.additionalInformation}
        />
      );
    }

    return (
      <Detail
        config={detail}
        product={product}
        rowClassName={rowClassName}
        additionalInformationClassName={style.additionalInformation}
      />
    );
  };

  return config && tabs.length ? (
    <Accordion className={cn(style.accordionDetails, className)} index={accordionIndexes}>
      {tabs.map((item) => (
        <Accordion.Item className={cn(style.accordionItem, classNameItem)} key={item.title} title={item.title}>
          <div className={style.accordionContent}>{renderContent(item)}</div>
        </Accordion.Item>
      ))}
    </Accordion>
  ) : null;
});

export default Tabs;
